import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { AnalyticsEntry } from 'app/api/analytics.api.service';
import { AnalyticsActions } from 'app/main/analytics/actions';
import { AnalyticsState } from 'app/main/analytics/analytics.types';
import { LoadingState } from 'app/utils/callstate';

export const adapter: EntityAdapter<AnalyticsEntry> = createEntityAdapter<AnalyticsEntry>(
    {
        selectId: entry => entry.key
    }
);

export const initialAnalyticsState: AnalyticsState = adapter.getInitialState({
    companyId: undefined,
    key: undefined,
    filter: undefined,
    callState: LoadingState.Init
});

export const reducer = createReducer(
    initialAnalyticsState,
    on(AnalyticsActions.analyticsRequested, (state, { query }): AnalyticsState => {
        return {
            ...state,
            companyId: query.companyId,
            key: query.key,
            filter: query.filter,
            callState: LoadingState.Loading
        };
    }),
    on(
        AnalyticsActions.analyticsLoaded,
        (state, { entries }): AnalyticsState => 
            state.key
            ? adapter.updateMany(
                entries.map((entry) => Object.assign({}, { id: entry.key, changes: entry })),
                {
                    ...state, callState: LoadingState.Loaded
                }
            )
            : adapter.addAll(
                entries,
                {
                    ...state, callState: LoadingState.Loaded
                }
            )
    ),
    on(
        AnalyticsActions.analyticsCancelled,
        (state, { errorMessage }): AnalyticsState => {
            return {
                ...state,
                callState: { errorMessage }
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryUpdateRequested,
        (state, _): AnalyticsState => {
            return {
                ...state,
                callState: LoadingState.Loading
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryUpdated,
        (state, { entry }): AnalyticsState => 
            adapter.updateOne(
                { id: entry.key, changes: entry },
                {
                    ...state,
                    callState: LoadingState.Loaded
                }
            )
    ),
    on(
        AnalyticsActions.analyticsEntryUpdateCancelled,
        (state, { errorMessage }): AnalyticsState => {
            return {
                ...state,
                callState: { errorMessage }
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryCreateRequested,
        (state, _): AnalyticsState => {
            return {
                ...state,
                callState: LoadingState.Loading
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryCreated,
        (state, { entry }): AnalyticsState => 
            adapter.addOne(
                entry,
                {
                    ...state,
                    callState: LoadingState.Loaded
                }
            )
    ),
    on(
        AnalyticsActions.analyticsEntryCreateCancelled,
        (state, { errorMessage }): AnalyticsState => {
            return {
                ...state,
                callState: { errorMessage }
            };
        }
    ),

    on(
        AnalyticsActions.analyticsEntryDeleteRequested,
        (state, _): AnalyticsState => {
            return {
                ...state,
                callState: LoadingState.Loading
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryDeleted,
        (state, { entry }): AnalyticsState => 
            adapter.removeOne(
                entry.key,
                {
                    ...state,
                    callState: LoadingState.Loaded
                }
            )
    ),
    on(
        AnalyticsActions.analyticsEntryDeleteCancelled,
        (state, { errorMessage }): AnalyticsState => {
            return {
                ...state,
                callState: { errorMessage }
            };
        }
    ),
    
    
    on(
        AnalyticsActions.analyticsEntryAddGroupRequested,
        (state, _): AnalyticsState => {
            return {
                ...state,
                callState: LoadingState.Loading
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryGroupAdded,
        (state, { entry }): AnalyticsState => 
            adapter.updateOne(
                { id: entry.key, changes: entry },
                {
                    ...state,
                    callState: LoadingState.Loaded
                }
            )
    ),
    on(
        AnalyticsActions.analyticsEntryAddGroupCancelled,
        (state, { errorMessage }): AnalyticsState => {
            return {
                ...state,
                callState: { errorMessage }
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryRemoveGroupRequested,
        (state, _): AnalyticsState => {
            return {
                ...state,
                callState: LoadingState.Loading
            };
        }
    ),
    on(
        AnalyticsActions.analyticsEntryGroupRemoved,
        (state, { entry }): AnalyticsState => 
            adapter.updateOne(
                { id: entry.key, changes: entry },
                {
                    ...state,
                    callState: LoadingState.Loaded
                }
            )
    ),
    on(
        AnalyticsActions.analyticsEntryRemoveGroupCancelled,
        (state, { errorMessage }): AnalyticsState => {
            return {
                ...state,
                callState: { errorMessage }
            };
        }
    ),
);
  
export const {
    selectAll,
    selectEntities,
    selectIds,
    selectTotal
} = adapter.getSelectors();
